import React, { useEffect, useState } from 'react'
import Typography from '@mui/material/Typography'
import { Alert } from '@mui/material'
import ButtonStartIcon from '../../../../../button/ButtonStartIcon'
import InputField from '../../../../../FormControl/input/InputField'
import Dialog from '../../../../../dialog/Dialog'
import useLcElements from '../../../../../../hooks/curriculum/useLcElements'
import useFetchData from '../../../../../../hooks/useFetchData'
import InputColor from '../../../../../FormControl/input/InputColor'

const LcElementDialog = ({ show, onClose, type, data = {} }) => {
  const { 
    errors: errorsData,
    createLcElement,
    updateLcElement,
    deleteLcElement
  } = useLcElements()
  const [form, setInputForm] = useState({})
  const [errors, setInputErrors] = useState(errorsData)
  const [loading, toggleLoading] = useState(false)
  const { isLoading: isLoadingMaxPosition, data: dataMaxPosition } = useFetchData('setting/lcElement/max-position')

  const onChange = (name, value) => {
    setInputForm({ ...form, [name]: value })
    setInputErrors({ ...errors, [name]: null })
  }

  const onSave = async (e) => {
    if (!['create', 'edit', 'delete'].includes(type))  {
      setInputErrors({ message: 'Invalid type' })
      return
    }
    e.preventDefault()
    toggleLoading(true)
    try {
      let result = { error: false } 
      if (type === 'create') {
        result = await createLcElement(form)
      }
      if (type === 'edit') {
        result = await updateLcElement(form._id, form)
      }
      if (type === 'delete') {
        result = await deleteLcElement(form._id)
      }
      if (result.error) {
        setInputErrors(result)
      } else {
        onClose()
      }
    } finally {
      toggleLoading(false)
    }
  }

  useEffect(() => {
    setInputErrors(errorsData)
  }, [errorsData])

  useEffect(() => {
    setInputForm(data || {})
  }, [data])

  useEffect(() => {
    if (isLoadingMaxPosition) return
    if (type === 'create' && dataMaxPosition) {
      setInputForm((old) => ({ ...old, position: dataMaxPosition }))
    }
  }, [isLoadingMaxPosition])

  const Actions = (
    <>
      <ButtonStartIcon
        onClick={onClose}
        color='primary'
        variant='outlined'
        disabled={loading}
        startIcon={["fal","times"]}
      >
        Cancel
      </ButtonStartIcon>
      { ['create', 'edit', 'delete'].includes(type) &&
        <ButtonStartIcon
        onClick={onSave}
        color='primary'
        variant='contained'
        disabled={loading}
        loading={loading}
        startIcon={["fal","check"]}
      >
        { type === 'delete' ? 'Confirm' : 'Save'}
      </ButtonStartIcon>
      }
    </>
  );
  
  return (
    <Dialog
      id={`${type}-lcElement`}
      show={show} 
      title={`${type} learning continuum element`}
      onClose={onClose}
      actions={Actions}
    >
      { type === 'delete'
        ? <Typography>Are you sure you want to delete the learning continuum element: <b>{form.name}</b>?</Typography>
        : <div className='flex flex-col gap-4 py-4'>
          <div className='grid grid-cols-4 items-start gap-4'>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Position
            </Typography>
            <div className='col-span-3'>
              { type === 'preview'
                ? <Typography>
                    {form.position}
                  </Typography>
                :  <InputField
                  id='position'
                  name='position'
                  value={form.position}
                  error={errors?.position}
                  helperText={errors?.position}
                  onChange={(e) => onChange(e.target.name, e.target.value)}
                  type='number'
                  fullWidth
                />
              }
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Name
            </Typography>
            <div className='col-span-3'>
              { type === 'preview'
                ? <Typography>
                    {form.name}
                  </Typography>
                :  <InputField
                  id='name'
                  name='name'
                  value={form.name}
                  error={errors?.name}
                  helperText={errors?.name}
                  onChange={(e) => onChange(e.target.name, e.target.value)}
                  fullWidth
                />
              }
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Short Name
            </Typography>
            <div className='col-span-3'>
              { type === 'preview'
                ? <Typography>
                    {form.shortName}
                  </Typography>
                :  <InputField
                  id='shortName'
                  name='shortName'
                  value={form.shortName}
                  error={errors?.shortName}
                  helperText={errors?.shortName}
                  onChange={(e) => onChange(e.target.name, e.target.value)}
                  fullWidth
                />
              }
            </div>
            <Typography variant='inherit' className='font-bold pl-2' color='primary' align='right' component='p'>
              Primary color
            </Typography>
            <div className='col-span-3'>
              <InputColor
                id='primaryColor'
                name='primaryColor'
                type="color"
                value={form.primaryColor}
                error={errors?.primaryColor}
                disabled={type === 'preview'}
                onChange={(value) => onChange("primaryColor", value)}
                fullWidth
              />
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Secondary color
            </Typography>
            <div className='col-span-3'>
              <InputColor
                id='secondaryColor'
                name='secondaryColor'
                type="color"
                value={form.secondaryColor}
                error={errors?.secondaryColor}
                disabled={type === 'preview'}
                onChange={(value) => onChange("secondaryColor", value)}
                fullWidth
              />
            </div>
          </div>
        </div>
      }
      { errors && errors.message && 
        <Alert 
          severity="error"
          onClose={() => setInputErrors({ message: null })}
        >
          {errors.message}
        </Alert> 
      }
    </Dialog>
  )
}

export default LcElementDialog